import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useScrollReveal } from '../hooks/useScrollReveal'
import { BbIcon } from '../components/BbIcon'
import type { BbIconName } from '../components/BbIcon'

/* Public landing page, ported from
   prototypes/batchbooks_enhanced_marketing_single_page/code.html.

   Static marketing copy only — nothing here talks to the API. The prototype's
   dead `href="#"` links have been pointed at real destinations: the CTAs go to
   /get-started and /login, and each "Learn more" deep-links to the section
   further down the page that expands on it, since there are no per-feature
   pages. Two things still need a decision from a human, noted inline below. */

const SOLUTIONS: Array<{
  tone: string
  icon: BbIconName
  title: string
  body: string
  points: string[]
  more: string
}> = [
  {
    tone: 'bb-tone-import',
    icon: 'upload',
    title: 'Bulk Import',
    body: 'Map and ingest complex Excel or CSV files directly into QuickBooks Online with pre-flight validation to ensure data integrity.',
    points: ['Drag & Drop interface', 'Smart column mapping', 'Pre-flight error checks'],
    more: '#features',
  },
  {
    tone: 'bb-tone-export',
    icon: 'download',
    title: 'Bulk Export',
    body: 'Extract clean, formatted data from QBO to Excel for advanced reporting, audits, or migration preparation.',
    points: ['Custom date ranges', 'Filter by entity/type', 'Excel workbooks'],
    more: '#automation',
  },
  {
    tone: 'bb-tone-delete',
    icon: 'trash',
    title: 'Bulk Delete',
    body: 'Safely identify and remove duplicate transactions or clean up erroneous imports with our guided, reversible delete wizard.',
    points: ['Safe preview mode', 'Rollback capabilities', 'Audit trail logging'],
    more: '#trust',
  },
]

const VALIDATION_FEATURES: Array<{ icon: BbIconName; title: string; body: string }> = [
  {
    icon: 'rule',
    title: 'Real-time Validation',
    body: 'Instantly highlights missing fields, invalid accounts, or duplicate entries as you edit.',
  },
  {
    icon: 'edit',
    title: 'Inline Editing',
    body: 'Correct data directly in the grid without re-uploading your entire file.',
  },
  {
    icon: 'done-all',
    title: 'Batch Submission',
    body: 'Select specific rows or push the entire validated dataset in one click.',
  },
]

const PIPELINE_FEATURES: Array<{ icon: BbIconName; title: string; body: string }> = [
  {
    icon: 'scanner',
    title: 'Precision OCR',
    body: 'Automatically extract data from PDFs and scanned invoices with high accuracy, transforming them into ready-to-import transactions.',
  },
  {
    icon: 'inbox',
    title: 'Dedicated Ingestion Email',
    body: 'Generate unique, secure email addresses for your clients. Any CSV or Excel file sent to this address is automatically routed to your mapping workflow.',
  },
  {
    icon: 'folder',
    title: 'Secure FTP/SFTP Integration',
    body: 'Connect your existing secure servers. BatchBooks watches specific folders and pulls in new transaction files the moment they arrive.',
  },
]

const TRUST: Array<{ icon: BbIconName; title: string; body: string }> = [
  {
    icon: 'history',
    title: 'Comprehensive Audit Logs',
    body: 'Every import, export, and deletion is meticulously logged with user IDs, timestamps, and row counts for full traceability.',
  },
  {
    icon: 'shield',
    title: 'HIPAA & GDPR Compliant',
    body: 'Our infrastructure meets rigorous compliance standards, ensuring your sensitive financial and client data remains strictly protected.',
  },
  {
    icon: 'undo',
    title: 'Safe Rollbacks',
    body: 'Made a mistake? Our reversible operations allow you to undo bulk deletes or imports, restoring your ledger to its previous state safely.',
  },
]

const TESTIMONIALS = [
  {
    quote:
      'BatchBooks cut our month-end reconciliation time in half. The bulk import validation catches errors before they ever hit QuickBooks.',
    initials: 'SJ',
    name: 'Sarah Jenkins',
    role: 'Managing Partner, Apex Accounting',
  },
  {
    quote:
      'The FTP pipeline feature is a game-changer. Clients drop their files on our secure server, and BatchBooks does the rest automatically.',
    initials: 'MR',
    name: 'Michael Ross',
    role: 'Lead Bookkeeper, Ross Financial',
  },
  {
    quote:
      'Knowing I have a safe rollback option when doing bulk deletes gives me peace of mind when cleaning up messy client files.',
    initials: 'EL',
    name: 'Elena Larsen',
    role: 'Independent CPA',
  },
]

const PLANS = [
  {
    name: 'Growth',
    price: '$49',
    blurb: 'Perfect for small businesses needing reliable bulk operations.',
    points: ['5,000 Credits /mo', '2 QBO Companies', '1 User Seat', 'Standard Support'],
    cta: 'Start Trial',
    featured: false,
  },
  {
    name: 'Scale',
    price: '$149',
    blurb: 'For growing firms managing multiple clients.',
    points: ['25,000 Credits /mo', '10 QBO Companies', '5 User Seats', 'Priority Support', 'Basic Automations'],
    cta: 'Start Trial',
    featured: true,
  },
  {
    name: 'Automate',
    price: '$399',
    blurb: 'Full power data pipelines for high-volume enterprises.',
    points: [
      '100,000 Credits /mo',
      'Unlimited QBO Companies',
      'Unlimited User Seats',
      'Precision OCR',
      'Email & FTP Pipelines',
    ],
    // The prototype labels this "Contact Sales" but there is no sales inbox or
    // contact route to send it to, so it self-serves like the others for now.
    cta: 'Start Trial',
    featured: false,
  },
]

const FAQS = [
  {
    q: 'How does BatchBooks connect to QuickBooks Online?',
    a: "We use Intuit's official OAuth 2.0 API for secure, token-based authentication. We never see or store your QuickBooks login credentials.",
  },
  {
    q: 'What happens if I accidentally import duplicates?',
    a: 'Our pre-flight validation catches exact duplicates before they import. If you need to revert an import, our audit log allows you to safely rollback specific batch jobs.',
  },
  {
    q: 'Can I manage multiple client accounts?',
    a: 'Yes! Our Scale and Automate plans are built for accounting firms. You can switch between connected QBO companies seamlessly from your dashboard.',
  },
  {
    q: 'Is there a limit on file size for imports?',
    a: 'We support CSV and Excel files up to 50MB per upload, which typically covers tens of thousands of transaction rows in a single batch.',
  },
]

/* The prototype's hero image is a temporary googleusercontent URL that will rot,
   so the illustration is drawn inline instead — same idea, no external asset.
   Fills reference the marketing.css custom properties rather than literal hex,
   so the drawing follows the palette if it ever moves. */
function HeroArt() {
  return (
    <svg viewBox="0 0 560 380" role="img" aria-label="Illustration of a validated transaction grid being pushed to QuickBooks Online">
      <defs>
        <linearGradient id="bb-hero-glow" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--bb-surface-container-high)" />
          <stop offset="100%" stopColor="var(--bb-surface)" />
        </linearGradient>
      </defs>
      <rect width="560" height="380" fill="url(#bb-hero-glow)" rx="8" />
      <g transform="translate(48 46)">
        <rect width="416" height="264" rx="6" fill="var(--bb-surface-lowest)" stroke="var(--bb-line)" />
        <rect width="416" height="40" rx="6" fill="var(--bb-grid-head)" />
        <rect y="34" width="416" height="6" fill="var(--bb-grid-head)" />
        <circle cx="22" cy="20" r="4" fill="var(--bb-outline-variant)" />
        <circle cx="38" cy="20" r="4" fill="var(--bb-outline-variant)" />
        <rect x="58" y="14" width="96" height="12" rx="3" fill="var(--bb-surface-container-highest)" />
        <rect x="330" y="12" width="70" height="16" rx="3" fill="var(--bb-green)" opacity="0.85" />
        {[0, 1, 2, 3, 4].map((row) => (
          <g key={row} transform={`translate(0 ${58 + row * 40})`}>
            <rect x="18" width="60" height="10" rx="2" fill="var(--bb-outline-variant)" />
            <rect x="96" width="112" height="10" rx="2" fill="var(--bb-line)" />
            <rect x="226" width="76" height="10" rx="2" fill="var(--bb-line)" />
            <rect x="320" width="48" height="10" rx="2" fill="var(--bb-inverse-primary)" />
            <circle cx="392" cy="5" r="6" fill={row === 2 ? 'var(--bb-red)' : 'var(--bb-green)'} opacity="0.9" />
            <rect x="18" y="26" width="380" height="1" fill="var(--bb-surface-container-low)" />
          </g>
        ))}
      </g>
      <g transform="translate(372 246)">
        <rect width="152" height="86" rx="6" fill="var(--bb-primary)" />
        <rect x="18" y="20" width="64" height="9" rx="2" fill="var(--bb-inverse-primary)" />
        <rect x="18" y="38" width="102" height="14" rx="3" fill="var(--bb-secondary-container)" opacity="0.9" />
        <rect x="18" y="60" width="84" height="8" rx="2" fill="var(--bb-primary-muted)" />
      </g>
    </svg>
  )
}

const NAV_SECTIONS = ['solutions', 'features', 'pricing', 'about']

export function MarketingPage() {
  const marketingRef = useRef<HTMLDivElement>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  useScrollReveal(marketingRef)

  useEffect(() => {
    const previous = document.title
    document.title = 'BatchBooks — Professional QuickBooks Automation'
    return () => {
      document.title = previous
    }
  }, [])

  useEffect(() => {
    const targets = NAV_SECTIONS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    )
    if (targets.length === 0 || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveSection(entry.target.id)
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    )
    targets.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const navLinkClass = (id: string) => `bb-nav-link${activeSection === id ? ' is-active' : ''}`

  return (
    <div className="bb">
      <div className="bb-marketing" ref={marketingRef}>
        <nav className="bb-nav" aria-label="Primary">
          <div className="bb-nav-left">
            <span className="bb-nav-brand">BatchBooks</span>
            <div className="bb-nav-links">
              <a className={navLinkClass('solutions')} href="#solutions">
                Solutions
              </a>
              <a className={navLinkClass('features')} href="#features">
                Features
              </a>
              <a className={navLinkClass('pricing')} href="#pricing">
                Pricing
              </a>
              <a className={navLinkClass('about')} href="#about">
                About
              </a>
            </div>
          </div>
          <div className="bb-nav-actions">
            <Link className="bb-nav-link bb-nav-signin" to="/login">
              Log In
            </Link>
            <Link className="bb-btn bb-btn-green" to="/get-started">
              Get Started
            </Link>
          </div>
        </nav>

        <header className="bb-hero">
          <div className="bb-hero-copy bb-scroll-reveal">
            <h1>Automate Your QuickBooks Workflow with Precision.</h1>
            <p>
              Seamlessly bulk import, export, and delete transactions. Save hours of manual data
              entry with our precision ledger tools designed for high-stakes financial operations.
            </p>
            <div className="bb-hero-actions">
              <Link className="bb-btn bb-btn-green bb-btn-lg" to="/get-started">
                Get Started for Free
                <BbIcon name="arrow-right" />
              </Link>
              {/* The prototype's second CTA is "View Documentation", but no docs
                  site is published yet, so it scrolls to the solutions section. */}
              <a className="bb-btn bb-btn-outline bb-btn-lg" href="#solutions">
                See how it works
              </a>
            </div>
            <div className="bb-hero-notes">
              <span className="bb-hero-note">
                <BbIcon name="check-circle" />
                No credit card required.
              </span>
              <span className="bb-hero-note">
                <BbIcon name="check-circle" />
                Connects to QuickBooks Online instantly.
              </span>
            </div>
          </div>
          <div className="bb-hero-art bb-scroll-reveal bb-scroll-reveal-scale bb-scroll-reveal-d2">
            <HeroArt />
          </div>
        </header>

        <section className="bb-section" id="solutions">
          <div className="bb-wrap">
            <div className="bb-section-head bb-scroll-reveal">
              <h2>Core Solutions</h2>
              <p>Linear, precise workflows to manage high-volume transactional data without errors.</p>
            </div>
            <div className="bb-grid-3">
              {SOLUTIONS.map((item, index) => (
                <article
                  className={`bb-card ${item.tone} bb-scroll-reveal bb-scroll-reveal-d${index + 1}`}
                  key={item.title}
                >
                  <span className="bb-card-rule" />
                  <span className="bb-card-icon">
                    <BbIcon name={item.icon} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                  <ul className="bb-ticks">
                    {item.points.map((point) => (
                      <li className="bb-tick" key={point}>
                        <BbIcon name="check" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  <a className="bb-more" href={item.more}>
                    Learn more
                    <BbIcon name="arrow-right" />
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bb-section bb-section-alt" id="features">
          <div className="bb-wrap">
            <div className="bb-split">
              <div className="bb-split-copy bb-scroll-reveal">
                <h2>Live Table Edit &amp; Validation</h2>
                <p>
                  Review, correct, and validate your data directly in an interactive spreadsheet view
                  before pushing to QuickBooks. Catch errors early with real-time feedback.
                </p>
                <ul className="bb-features">
                  {VALIDATION_FEATURES.map((feature) => (
                    <li className="bb-feature" key={feature.title}>
                      <span className="bb-feature-icon">
                        <BbIcon name={feature.icon} />
                      </span>
                      <div>
                        <h4>{feature.title}</h4>
                        <p>{feature.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bb-panel bb-scroll-reveal bb-scroll-reveal-scale bb-scroll-reveal-d2" aria-hidden="true">
                <div className="bb-panel-head">
                  <span className="bb-panel-title">Pre-flight Validation Grid</span>
                  <span className="bb-panel-tags">
                    <span className="bb-pill bb-pill-err">2 Errors</span>
                    <span className="bb-pill bb-pill-ok">14 Valid</span>
                  </span>
                </div>
                <table className="bb-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Payee</th>
                      <th>Account</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="bb-num">2023-10-24</td>
                      <td>Amazon Web Services</td>
                      <td>Software Subscriptions</td>
                      <td className="bb-num">$450.00</td>
                      <td className="bb-status-ok">
                        <BbIcon name="check-circle" />
                      </td>
                    </tr>
                    <tr className="bb-row-bad">
                      <td className="bb-num">2023-10-25</td>
                      <td>Staples</td>
                      <td>
                        <span className="bb-cell-bad">
                          <BbIcon name="warning" />
                          Unknown Account
                        </span>
                      </td>
                      <td className="bb-num">$124.50</td>
                      <td className="bb-status-bad">
                        <BbIcon name="x-circle" />
                      </td>
                    </tr>
                    <tr>
                      <td className="bb-num">2023-10-25</td>
                      <td>Uber</td>
                      <td>Travel &amp; Meals</td>
                      <td className="bb-num">$45.20</td>
                      <td className="bb-status-ok">
                        <BbIcon name="check-circle" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="bb-section" id="automation">
          <div className="bb-wrap">
            <div className="bb-split bb-split-flip">
              <div className="bb-split-copy bb-scroll-reveal">
                <h2>Hands-off Data Pipelines</h2>
                <p>
                  Move beyond manual imports. Set up intelligent, recurring data pipelines that
                  process incoming files automatically via email or secure server drops.
                </p>
                <ul className="bb-features">
                  {PIPELINE_FEATURES.map((feature) => (
                    <li className="bb-feature" key={feature.title}>
                      <span className="bb-feature-icon">
                        <BbIcon name={feature.icon} />
                      </span>
                      <div>
                        <h4>{feature.title}</h4>
                        <p>{feature.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bb-panel bb-scroll-reveal bb-scroll-reveal-scale bb-scroll-reveal-d2" aria-hidden="true">
                <div className="bb-panel-head">
                  <span className="bb-panel-title">Active Pipelines</span>
                  <span className="bb-panel-add">
                    <BbIcon name="plus" />
                    New Pipeline
                  </span>
                </div>
                <div className="bb-pipes">
                  <div className="bb-pipe">
                    <span className="bb-pipe-left">
                      <BbIcon name="mail" />
                      <span>
                        <span className="bb-pipe-name">Daily Invoices</span>
                        <span className="bb-pipe-meta">invoices@batchbooks.app</span>
                      </span>
                    </span>
                    <span className="bb-pill bb-pill-active">ACTIVE</span>
                  </div>
                  <div className="bb-pipe">
                    <span className="bb-pipe-left">
                      <BbIcon name="folder" />
                      <span>
                        <span className="bb-pipe-name">Weekly Statements</span>
                        <span className="bb-pipe-meta">FTP /client-drops/statements</span>
                      </span>
                    </span>
                    <span className="bb-pill bb-pill-active">ACTIVE</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bb-section bb-section-alt" id="trust">
          <div className="bb-wrap">
            <div className="bb-section-head bb-scroll-reveal">
              <h2>Enterprise-Grade Reliability</h2>
              <p>Built for high-stakes financial data. Your security and auditability are our top priority.</p>
            </div>
            <div className="bb-grid-3">
              {TRUST.map((item, index) => (
                <article className={`bb-card bb-trust-card bb-scroll-reveal bb-scroll-reveal-d${index + 1}`} key={item.title}>
                  <span className="bb-trust-icon">
                    <BbIcon name={item.icon} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bb-section" id="testimonials">
          <div className="bb-wrap">
            <div className="bb-section-head bb-scroll-reveal">
              <h2>Trusted by Professionals</h2>
              <p>See how accountants and bookkeepers are saving hours every week.</p>
            </div>
            <div className="bb-grid-3">
              {TESTIMONIALS.map((item, index) => (
                <figure className={`bb-quote-card bb-scroll-reveal bb-scroll-reveal-d${index + 1}`} key={item.name}>
                  <blockquote>&ldquo;{item.quote}&rdquo;</blockquote>
                  <figcaption className="bb-quote-by">
                    <span className="bb-avatar" aria-hidden="true">
                      {item.initials}
                    </span>
                    <span>
                      <span className="bb-quote-name">{item.name}</span>
                      <span className="bb-quote-role">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bb-section bb-section-alt" id="pricing">
          <div className="bb-wrap">
            <div className="bb-section-head bb-scroll-reveal">
              <h2>Transparent Pricing</h2>
              <p>Choose the plan that fits your transaction volume and automation needs.</p>
            </div>
            <div className="bb-plans">
              {PLANS.map((plan, index) => (
                <article
                  className={`bb-plan${plan.featured ? ' bb-plan-featured' : ''} bb-scroll-reveal bb-scroll-reveal-d${index + 1}`}
                  key={plan.name}
                >
                  {plan.featured ? <span className="bb-plan-flag">Most Popular</span> : null}
                  <h3>{plan.name}</h3>
                  <div className="bb-plan-price">
                    <span className="bb-plan-amount">{plan.price}</span>
                    <span className="bb-plan-per">/mo</span>
                  </div>
                  <p>{plan.blurb}</p>
                  <ul className="bb-ticks">
                    {plan.points.map((point) => (
                      <li className="bb-tick" key={point}>
                        <BbIcon name="check" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  <Link
                    className={`bb-btn ${plan.featured ? 'bb-btn-green' : 'bb-btn-outline'}`}
                    to="/get-started"
                  >
                    {plan.cta}
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bb-section" id="faq">
          <div className="bb-wrap">
            <div className="bb-section-head bb-scroll-reveal">
              <h2>Frequently Asked Questions</h2>
            </div>
            <div className="bb-faq">
              {FAQS.map((item, index) => (
                <article className={`bb-faq-item bb-scroll-reveal bb-scroll-reveal-d${Math.min(index + 1, 6)}`} key={item.q}>
                  <h3 className="bb-faq-q">{item.q}</h3>
                  <p className="bb-faq-a">{item.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <footer className="bb-footer bb-scroll-reveal" id="about">
          <div className="bb-footer-in">
            <div>© {new Date().getFullYear()} QBO BulkOps Professional. All rights reserved.</div>
            <div className="bb-footer-links">
              <a href="#solutions">Solutions</a>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <Link to="/login">Log In</Link>
              <Link to="/get-started">Get Started</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
